import {
  Excalidraw,
  LiveCollaborationTrigger,
  TTDDialogTrigger,
  CaptureUpdateAction,
  reconcileElements,
  useEditorInterface,
} from "@excalidraw/excalidraw";
import { trackEvent } from "@excalidraw/excalidraw/analytics";
import { getDefaultAppState } from "@excalidraw/excalidraw/appState";
import {
  CommandPalette,
  DEFAULT_CATEGORIES,
} from "@excalidraw/excalidraw/components/CommandPalette/CommandPalette";
import { ErrorDialog } from "@excalidraw/excalidraw/components/ErrorDialog";
import { OverwriteConfirmDialog } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirm";
import { openConfirmModal } from "@excalidraw/excalidraw/components/OverwriteConfirm/OverwriteConfirmState";
import { ShareableLinkDialog } from "@excalidraw/excalidraw/components/ShareableLinkDialog";
import Trans from "@excalidraw/excalidraw/components/Trans";
import {
  APP_NAME,
  EVENT,
  THEME,
  VERSION_TIMEOUT,
  debounce,
  getVersion,
  getFrame,
  isTestEnv,
  preventUnload,
  resolvablePromise,
  isRunningInIframe,
  isDevEnv,
} from "@excalidraw/common";
import polyfill from "@excalidraw/excalidraw/polyfill";
import { useCallback, useEffect, useRef, useState } from "react";
import { loadFromBlob } from "@excalidraw/excalidraw/data/blob";
import { useCallbackRefState } from "@excalidraw/excalidraw/hooks/useCallbackRefState";
import { t } from "@excalidraw/excalidraw/i18n";

import {
  GithubIcon,
  XBrandIcon,
  DiscordIcon,
  ExcalLogo,
  usersIcon,
  exportToPlus,
  share,
  youtubeIcon,
} from "@excalidraw/excalidraw/components/icons";
import { isElementLink } from "@excalidraw/element";
import {
  bumpElementVersions,
  restoreAppState,
  restoreElements,
} from "@excalidraw/excalidraw/data/restore";
import { newElementWith } from "@excalidraw/element";
import { isInitializedImageElement } from "@excalidraw/element";
import clsx from "clsx";
import {
  parseLibraryTokensFromUrl,
  useHandleLibrary,
} from "@excalidraw/excalidraw/data/library";

import type { RemoteExcalidrawElement } from "@excalidraw/excalidraw/data/reconcile";
import type { RestoredDataState } from "@excalidraw/excalidraw/data/restore";
import type {
  FileId,
  NonDeletedExcalidrawElement,
  OrderedExcalidrawElement,
} from "@excalidraw/element/types";
import type {
  AppState,
  ExcalidrawImperativeAPI,
  BinaryFiles,
  ExcalidrawInitialDataState,
  UIAppState,
} from "@excalidraw/excalidraw/types";
import type { ResolutionType } from "@excalidraw/common/utility-types";
import type { ResolvablePromise } from "@excalidraw/common/utils";

import CustomStats from "./CustomStats";
import {
  Provider,
  useAtom,
  useAtomValue,
  useAtomWithInitialValue,
  appJotaiStore,
} from "./app-jotai";
import {
  FIREBASE_STORAGE_PREFIXES,
  isExcalidrawPlusSignedUser,
  STORAGE_KEYS,
  SYNC_BROWSER_TABS_TIMEOUT,
} from "./app_constants";
import {
  isLoggedIn as checkIsLoggedIn,
  apiGetMe,
  apiGetDrawing,
  apiUpdateDrawing,
  apiUpdateDrawingKeepAlive,
  apiSaveFiles,
  apiGetSharedDrawing,
} from "./data/api";
import { generateThumbnail } from "./data/thumbnail";
import {
  apiListTags,
  apiSetDrawingTags,
  apiCreateTag,
  apiRespondCollabJoin,
  apiEndCollabSession,
  apiListNotifications,
  apiMarkNotificationRead,
} from "./data/api";
import { DrawingStore } from "./data/DrawingStore";
import { PendingSyncStore, computeVersionSum } from "./data/PendingSyncStore";
import { AuthDialog } from "./components/AuthDialog";
import { DrawingsDashboard } from "./components/DrawingsDashboard";
import { AdminPanel } from "./components/AdminPanel";
import { ElementComments } from "./components/ElementComments";
import { CollabRequestPopup } from "./components/CollabRequestPopup";
import "./components/DrawingsDashboard.scss";

import type { ApiUser, SharedDrawing, TagInfo } from "./data/api";
import Collab, {
  activeRoomLinkAtom,
  collabAPIAtom,
  isCollaboratingAtom,
  isOfflineAtom,
} from "./collab/Collab";
import { AppFooter } from "./components/AppFooter";
import { AppMainMenu } from "./components/AppMainMenu";
import { AppWelcomeScreen } from "./components/AppWelcomeScreen";
import {
  ExportToExcalidrawPlus,
  exportToExcalidrawPlus,
} from "./components/ExportToExcalidrawPlus";
import { TopErrorBoundary } from "./components/TopErrorBoundary";

import {
  exportToBackend,
  getCollaborationLinkData,
  importFromBackend,
  isCollaborationLink,
} from "./data";

import { updateStaleImageStatuses } from "./data/FileManager";
import {
  importFromLocalStorage,
  importUsernameFromLocalStorage,
} from "./data/localStorage";

import { loadFilesFromFirebase } from "./data/firebase";
import {
  LocalData,
  localStorageQuotaExceededAtom,
} from "./data/LocalData";
import { LibraryServerAdapter } from "./data/LibraryServerAdapter";
import { LibraryIDBMigrationAdapter } from "./data/LibraryIDBMigrationAdapter";
import { isBrowserStorageStateNewer } from "./data/tabSync";
import { ShareDialog, shareDialogStateAtom } from "./share/ShareDialog";
import CollabError, { collabErrorIndicatorAtom } from "./collab/CollabError";
import { useHandleAppTheme } from "./useHandleAppTheme";
import { getPreferredLanguage } from "./app-language/language-detector";
import { useAppLangCode } from "./app-language/language-state";
import DebugCanvas, {
  debugRenderer,
  isVisualDebuggerEnabled,
  loadSavedDebugState,
} from "./components/DebugCanvas";
import { AIComponents } from "./components/AI";
import { ExcalidrawPlusIframeExport } from "./ExcalidrawPlusIframeExport";

import "./index.scss";

import { ExcalidrawPlusPromoBanner } from "./components/ExcalidrawPlusPromoBanner";
import { AppSidebar } from "./components/AppSidebar";

import type { CollabAPI } from "./collab/Collab";

polyfill();

window.EXCALIDRAW_THROTTLE_RENDER = true;

declare global {
  interface BeforeInstallPromptEventChoiceResult {
    outcome: "accepted" | "dismissed";
  }

  interface BeforeInstallPromptEvent extends Event {
    prompt(): Promise<void>;
    userChoice: Promise<BeforeInstallPromptEventChoiceResult>;
  }

  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

let pwaEvent: BeforeInstallPromptEvent | null = null;

// Adding a listener outside of the component as it may (?) need to be
// subscribed early to catch the event.
//
// Also note that it will fire only if certain heuristics are met (user has
// used the app for some time, etc.)
window.addEventListener(
  "beforeinstallprompt",
  (event: BeforeInstallPromptEvent) => {
    // prevent Chrome <= 67 from automatically showing the prompt
    event.preventDefault();
    // cache for later use
    pwaEvent = event;
  },
);

let isSelfEmbedding = false;

if (window.self !== window.top) {
  try {
    const parentUrl = new URL(document.referrer);
    const currentUrl = new URL(window.location.href);
    if (parentUrl.origin === currentUrl.origin) {
      isSelfEmbedding = true;
    }
  } catch (error) {
    // ignore
  }
}

const shareableLinkConfirmDialog = {
  title: t("overwriteConfirm.modal.shareableLink.title"),
  description: (
    <Trans
      i18nKey="overwriteConfirm.modal.shareableLink.description"
      bold={(text) => <strong>{text}</strong>}
      br={() => <br />}
    />
  ),
  actionLabel: t("overwriteConfirm.modal.shareableLink.button"),
  color: "danger",
} as const;

const initializeScene = async (opts: {
  collabAPI: CollabAPI | null;
  excalidrawAPI: ExcalidrawImperativeAPI;
}): Promise<
  { scene: ExcalidrawInitialDataState | null } & (
    | { isExternalScene: true; id: string; key: string }
    | { isExternalScene: false; id?: null; key?: null }
  )
> => {
  const searchParams = new URLSearchParams(window.location.search);
  const id = searchParams.get("id");
  const jsonBackendMatch = window.location.hash.match(
    /^#json=([a-zA-Z0-9_-]+),([a-zA-Z0-9_-]+)$/,
  );
  const externalUrlMatch = window.location.hash.match(/^#url=(.*)$/);

  const localDataState = importFromLocalStorage();

  let scene: Omit<
    RestoredDataState,
    // we're not storing files in the scene database/localStorage, and instead
    // fetch them async from a different store
    "files"
  > & {
    scrollToContent?: boolean;
  } = {
    elements: restoreElements(localDataState?.elements, null, {
      repairBindings: true,
      deleteInvisibleElements: true,
    }),
    appState: restoreAppState(localDataState?.appState, null),
  };

  let roomLinkData = getCollaborationLinkData(window.location.href);
  const isExternalScene = !!(id || jsonBackendMatch || roomLinkData);
  if (isExternalScene) {
    if (
      // don't prompt if scene is empty
      !scene.elements.length ||
      // don't prompt for collab scenes because we don't override local storage
      roomLinkData ||
      // otherwise, prompt whether user wants to override current scene
      (await openConfirmModal(shareableLinkConfirmDialog))
    ) {
      if (jsonBackendMatch) {
        const imported = await importFromBackend(
          jsonBackendMatch[1],
          jsonBackendMatch[2],
        );

        scene = {
          elements: bumpElementVersions(
            restoreElements(imported.elements, null, {
              repairBindings: true,
              deleteInvisibleElements: true,
            }),
            localDataState?.elements,
          ),
          appState: restoreAppState(
            imported.appState,
            // local appState when importing from backend to ensure we restore
            // localStorage user settings which we do not persist on server.
            localDataState?.appState,
          ),
        };
      }
      scene.scrollToContent = true;
      if (!roomLinkData) {
        window.history.replaceState({}, APP_NAME, window.location.origin);
      }
    } else {
      // https://github.com/excalidraw/excalidraw/issues/1919
      if (document.hidden) {
        return new Promise((resolve, reject) => {
          window.addEventListener(
            "focus",
            () => initializeScene(opts).then(resolve).catch(reject),
            {
              once: true,
            },
          );
        });
      }

      roomLinkData = null;
      window.history.replaceState({}, APP_NAME, window.location.origin);
    }
  } else if (externalUrlMatch) {
    window.history.replaceState({}, APP_NAME, window.location.origin);

    const url = externalUrlMatch[1];
    try {
      const request = await fetch(window.decodeURIComponent(url));
      const data = await loadFromBlob(await request.blob(), null, null);
      if (
        !scene.elements.length ||
        (await openConfirmModal(shareableLinkConfirmDialog))
      ) {
        return { scene: data, isExternalScene };
      }
    } catch (error: any) {
      return {
        scene: {
          appState: {
            errorMessage: t("alerts.invalidSceneUrl"),
          },
        },
        isExternalScene,
      };
    }
  }

  if (roomLinkData && opts.collabAPI) {
    const { excalidrawAPI } = opts;

    const scene = await opts.collabAPI.startCollaboration(roomLinkData);

    return {
      // when collaborating, the state may have already been updated at this
      // point (we may have received updates from other clients), so reconcile
      // elements and appState with existing state
      scene: {
        ...scene,
        appState: {
          ...restoreAppState(
            {
              ...scene?.appState,
              theme: localDataState?.appState?.theme || scene?.appState?.theme,
            },
            excalidrawAPI.getAppState(),
          ),
          // necessary if we're invoking from a hashchange handler which doesn't
          // go through App.initializeScene() that resets this flag
          isLoading: false,
        },
        elements: reconcileElements(
          scene?.elements || [],
          excalidrawAPI.getSceneElementsIncludingDeleted() as RemoteExcalidrawElement[],
          excalidrawAPI.getAppState(),
        ),
      },
      isExternalScene: true,
      id: roomLinkData.roomId,
      key: roomLinkData.roomKey,
    };
  } else if (scene) {
    return isExternalScene && jsonBackendMatch
      ? {
          scene,
          isExternalScene,
          id: jsonBackendMatch[1],
          key: jsonBackendMatch[2],
        }
      : { scene, isExternalScene: false };
  }
  return { scene: null, isExternalScene: false };
};

// Module-level handle to the active Excalidraw API so code outside the wrapper
// component (like ExcalidrawApp.handleBackToDashboard) can grab the current
// scene to render a thumbnail before leaving.
let activeExcalidrawAPI: ExcalidrawImperativeAPI | null = null;

const ExcalidrawWrapper = () => {
  const [errorMessage, setErrorMessage] = useState("");
  const isCollabDisabled = isRunningInIframe();

  const { editorTheme, appTheme, setAppTheme } = useHandleAppTheme();

  const [langCode, setLangCode] = useAppLangCode();

  const editorInterface = useEditorInterface();

  // initial state
  // ---------------------------------------------------------------------------

  const initialStatePromiseRef = useRef<{
    promise: ResolvablePromise<ExcalidrawInitialDataState | null>;
  }>({ promise: null! });
  if (!initialStatePromiseRef.current.promise) {
    initialStatePromiseRef.current.promise =
      resolvablePromise<ExcalidrawInitialDataState | null>();
  }

  // Guard: prevent cloud sync until initializeScene resolves and Excalidraw
  // processes the initial data (avoids overwriting DB with empty data on F5)
  const cloudSyncEnabledRef = useRef(false);

  const debugCanvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    trackEvent("load", "frame", getFrame());
    // Delayed so that the app has a time to load the latest SW
    setTimeout(() => {
      trackEvent("load", "version", getVersion());
    }, VERSION_TIMEOUT);
  }, []);

  const [excalidrawAPI, excalidrawRefCallback] =
    useCallbackRefState<ExcalidrawImperativeAPI>();

  useEffect(() => {
    activeExcalidrawAPI = excalidrawAPI ?? null;
    return () => {
      if (activeExcalidrawAPI === excalidrawAPI) {
        activeExcalidrawAPI = null;
      }
    };
  }, [excalidrawAPI]);

  const [, setShareDialogState] = useAtom(shareDialogStateAtom);
  const [collabAPI] = useAtom(collabAPIAtom);

  // Flag one-shot setado pelo NotificationCenter para forçar viewMode quando
  // o destinatário escolhe "Apenas visualizar".
  const [forceViewMode, setForceViewMode] = useState(false);

  useEffect(() => {
    if (!excalidrawAPI) return;
    const target = sessionStorage.getItem("excalidraw-open-as-view-only");
    const current = DrawingStore.getActiveDrawingId();
    if (target && target === current) {
      setForceViewMode(true);
      sessionStorage.removeItem("excalidraw-open-as-view-only");
    }
  }, [excalidrawAPI]);

  // Quando Alice aceita um pedido de colaboração, o NotificationCenter seta
  // `excalidraw-pending-respond-join` = JSON {drawingId, recipientUserId} e
  // leva-a pro editor do drawing. Aqui, depois que collabAPI está pronto:
  //   1. Iniciamos a sala (Alice é a primeira na sala — com o conteúdo dela)
  //   2. Pegamos o roomUrl resultante
  //   3. Chamamos respond-join pra entregar a URL pro Bob (que está em polling)
  useEffect(() => {
    if (!excalidrawAPI || !collabAPI) return;
    const raw = sessionStorage.getItem("excalidraw-pending-respond-join");
    if (!raw) return;
    let parsed: { drawingId: string; recipientUserId: string } | null = null;
    try {
      parsed = JSON.parse(raw);
    } catch {
      sessionStorage.removeItem("excalidraw-pending-respond-join");
      return;
    }
    if (!parsed) return;
    const current = DrawingStore.getActiveDrawingId();
    if (parsed.drawingId !== current) return;
    sessionStorage.removeItem("excalidraw-pending-respond-join");

    const t = window.setTimeout(async () => {
      try {
        if (!collabAPI.isCollaborating()) {
          await collabAPI.startCollaboration(null);
          await new Promise((r) => window.setTimeout(r, 300));
        }
        const roomUrl = window.location.href;
        if (!/#room=/.test(roomUrl)) {
          console.error("collab room URL ainda não disponível");
          return;
        }
        await apiRespondCollabJoin(parsed!.drawingId, {
          recipientUserId: parsed!.recipientUserId,
          roomUrl,
        });
      } catch (err) {
        console.error("falha ao aceitar pedido de colab:", err);
      }
    }, 500);
    return () => window.clearTimeout(t);
  }, [excalidrawAPI, collabAPI]);
  const [isCollaborating] = useAtomWithInitialValue(isCollaboratingAtom, () => {
    return isCollaborationLink(window.location.href);
  });
  const activeRoomLink = useAtomValue(activeRoomLinkAtom);
  const collabError = useAtomValue(collabErrorIndicatorAtom);

  // Quando o dono (Alice) para a sessão, dispara o end-session no server para
  // notificar os demais. Detectamos pela transição activeRoomLink: string → null.
  // Server confere ownership; se não for dono, é no-op silencioso.
  const lastActiveRoomLinkRef = useRef<string | null>(null);
  useEffect(() => {
    if (activeRoomLink) {
      lastActiveRoomLinkRef.current = activeRoomLink;
      return;
    }
    const prev = lastActiveRoomLinkRef.current;
    if (!prev) return;
    lastActiveRoomLinkRef.current = null;
    const drawingId = DrawingStore.getActiveDrawingId();
    if (drawingId && checkIsLoggedIn()) {
      apiEndCollabSession(drawingId, prev).catch((err) => {
        console.error("Falha ao notificar fim da sessão de colab:", err);
      });
    }
  }, [activeRoomLink]);

  // Watcher (lado convidado): enquanto numa sala, faz polling de notificações
  // collab_session_ended. Se o hash da sala (#room=...) bater com a atual,
  // mostra toast e desconecta o socket localmente.
  // Comparamos só o hash porque Alice e Bob têm pathnames diferentes (Bob
  // entra via window.location.hash = url.hash, preservando o próprio path).
  useEffect(() => {
    if (!collabAPI || !excalidrawAPI || !activeRoomLink || !checkIsLoggedIn()) {
      return;
    }
    const extractRoomHash = (url?: string | null): string | null => {
      if (!url) return null;
      try {
        const hash = new URL(url, window.location.origin).hash;
        return /^#room=/.test(hash) ? hash : null;
      } catch {
        return null;
      }
    };
    const currentRoomHash = extractRoomHash(activeRoomLink);
    if (!currentRoomHash) return;

    let cancelled = false;
    let handled = false;

    const tick = async () => {
      if (cancelled || handled) return;
      try {
        const list = await apiListNotifications();
        const match = list.find(
          (n) =>
            n.type === "collab_session_ended" &&
            !n.read_at &&
            extractRoomHash(n.payload.roomUrl) === currentRoomHash,
        );
        if (!match || cancelled) return;
        handled = true;
        await apiMarkNotificationRead(match.id).catch(() => {});
        excalidrawAPI.setToast({
          message: `${match.payload.fromName} encerrou a sessão de colaboração`,
          duration: 5000,
        });
        if (collabAPI.isCollaborating()) {
          collabAPI.stopCollaboration(false);
        }
      } catch {
        /* silencioso */
      }
    };
    tick();
    const handle = window.setInterval(tick, 2_500);
    return () => {
      cancelled = true;
      window.clearInterval(handle);
    };
  }, [collabAPI, excalidrawAPI, activeRoomLink]);

  useHandleLibrary({
    excalidrawAPI,
    adapter: LibraryServerAdapter,
    // One-shot: read legacy per-browser library from IndexedDB on first run
    // after deploy, upload it to the shared Techify library, then drop the IDB.
    migrationAdapter: LibraryIDBMigrationAdapter,
  });

  const [, forceRefresh] = useState(false);

  useEffect(() => {
    if (isDevEnv()) {
      const debugState = loadSavedDebugState();

      if (debugState.enabled && !window.visualDebug) {
        window.visualDebug = {
          data: [],
        };
      } else {
        delete window.visualDebug;
      }
      forceRefresh((prev) => !prev);
    }
  }, [excalidrawAPI]);

  useEffect(() => {
    if (!excalidrawAPI || (!isCollabDisabled && !collabAPI)) {
      return;
    }

    const loadImages = (
      data: ResolutionType<typeof initializeScene>,
      isInitialLoad = false,
    ) => {
      if (!data.scene) {
        return;
      }
      if (collabAPI?.isCollaborating()) {
        if (data.scene.elements) {
          collabAPI
            .fetchImageFilesFromFirebase({
              elements: data.scene.elements,
              forceFetchFiles: true,
            })
            .then(({ loadedFiles, erroredFiles }) => {
              excalidrawAPI.addFiles(loadedFiles);
              updateStaleImageStatuses({
                excalidrawAPI,
                erroredFiles,
                elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
              });
            });
        }
      } else {
        const fileIds =
          data.scene.elements?.reduce((acc, element) => {
            if (isInitializedImageElement(element)) {
              return acc.concat(element.fileId);
            }
            return acc;
          }, [] as FileId[]) || [];

        if (data.isExternalScene) {
          loadFilesFromFirebase(
            `${FIREBASE_STORAGE_PREFIXES.shareLinkFiles}/${data.id}`,
            data.key,
            fileIds,
          ).then(({ loadedFiles, erroredFiles }) => {
            excalidrawAPI.addFiles(loadedFiles);
            updateStaleImageStatuses({
              excalidrawAPI,
              erroredFiles,
              elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
            });
          });
        } else if (isInitialLoad) {
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
          // on fresh load, clear unused files from IDB (from previous
          // session)
          LocalData.fileStorage.clearObsoleteFiles({ currentFileIds: fileIds });
        }
      }
    };

    initializeScene({ collabAPI, excalidrawAPI }).then(async (data) => {
      loadImages(data, /* isInitialLoad */ true);
      initialStatePromiseRef.current.promise.resolve(data.scene);
      // Enable cloud sync only after Excalidraw has processed initialData,
      // preventing the first onChange (with empty/stale data) from overwriting the DB
      requestAnimationFrame(() => {
        cloudSyncEnabledRef.current = true;
      });
    });

    const onHashChange = async (event: HashChangeEvent) => {
      event.preventDefault();
      // Ignore drawing hash changes — handled by ExcalidrawApp
      if (isDrawingHash(window.location.hash)) {
        return;
      }
      const libraryUrlTokens = parseLibraryTokensFromUrl();
      if (!libraryUrlTokens) {
        if (
          collabAPI?.isCollaborating() &&
          !isCollaborationLink(window.location.href)
        ) {
          collabAPI.stopCollaboration(false);
        }
        excalidrawAPI.updateScene({ appState: { isLoading: true } });

        initializeScene({ collabAPI, excalidrawAPI }).then((data) => {
          loadImages(data);
          if (data.scene) {
            excalidrawAPI.updateScene({
              elements: restoreElements(data.scene.elements, null, {
                repairBindings: true,
              }),
              appState: restoreAppState(data.scene.appState, null),
              captureUpdate: CaptureUpdateAction.IMMEDIATELY,
            });
          }
        });
      }
    };

    const syncData = debounce(() => {
      if (isTestEnv()) {
        return;
      }
      if (
        !document.hidden &&
        ((collabAPI && !collabAPI.isCollaborating()) || isCollabDisabled)
      ) {
        // don't sync if local state is newer or identical to browser state
        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_DATA_STATE)) {
          const localDataState = importFromLocalStorage();
          const username = importUsernameFromLocalStorage();
          setLangCode(getPreferredLanguage());
          excalidrawAPI.updateScene({
            ...localDataState,
            captureUpdate: CaptureUpdateAction.NEVER,
          });
          LibraryServerAdapter.load({ source: "load" }).then((data) => {
            if (data) {
              excalidrawAPI.updateLibrary({
                libraryItems: data.libraryItems,
              });
            }
          });
          collabAPI?.setUsername(username || "");
        }

        if (isBrowserStorageStateNewer(STORAGE_KEYS.VERSION_FILES)) {
          const elements = excalidrawAPI.getSceneElementsIncludingDeleted();
          const currFiles = excalidrawAPI.getFiles();
          const fileIds =
            elements?.reduce((acc, element) => {
              if (
                isInitializedImageElement(element) &&
                // only load and update images that aren't already loaded
                !currFiles[element.fileId]
              ) {
                return acc.concat(element.fileId);
              }
              return acc;
            }, [] as FileId[]) || [];
          if (fileIds.length) {
            LocalData.fileStorage
              .getFiles(fileIds)
              .then(({ loadedFiles, erroredFiles }) => {
                if (loadedFiles.length) {
                  excalidrawAPI.addFiles(loadedFiles);
                }
                updateStaleImageStatuses({
                  excalidrawAPI,
                  erroredFiles,
                  elements: excalidrawAPI.getSceneElementsIncludingDeleted(),
                });
              });
          }
        }
      }
    }, SYNC_BROWSER_TABS_TIMEOUT);

    const onUnload = () => {
      LocalData.flushSave();
      flushCloudSync(true);
    };

    const visibilityChange = (event: FocusEvent | Event) => {
      if (event.type === EVENT.BLUR || document.hidden) {
        LocalData.flushSave();
        flushCloudSync();
      }
      if (
        event.type === EVENT.VISIBILITY_CHANGE ||
        event.type === EVENT.FOCUS
      ) {
        syncData();
      }
    };

    window.addEventListener(EVENT.HASHCHANGE, onHashChange, false);
    window.addEventListener(EVENT.UNLOAD, onUnload, false);
    window.addEventListener(EVENT.BLUR, visibilityChange, false);
    document.addEventListener(EVENT.VISIBILITY_CHANGE, visibilityChange, false);
    window.addEventListener(EVENT.FOCUS, visibilityChange, false);
    return () => {
      window.removeEventListener(EVENT.HASHCHANGE, onHashChange, false);
      window.removeEventListener(EVENT.UNLOAD, onUnload, false);
      window.removeEventListener(EVENT.BLUR, visibilityChange, false);
      window.removeEventListener(EVENT.FOCUS, visibilityChange, false);
      document.removeEventListener(
        EVENT.VISIBILITY_CHANGE,
        visibilityChange,
        false,
      );
      // Clean up module-level cloud sync state on unmount
      if (cloudSyncTimer) {
        clearTimeout(cloudSyncTimer);
        cloudSyncTimer = null;
      }
      pendingCloudSync = null;
      cloudSyncEnabledRef.current = false;
    };
  }, [isCollabDisabled, collabAPI, excalidrawAPI, setLangCode]);

  useEffect(() => {
    const unloadHandler = (event: BeforeUnloadEvent) => {
      LocalData.flushSave();
      // Capture before flushCloudSync so we know if there was unsynced data.
      const hadPendingCloudSync = pendingCloudSync !== null;
      flushCloudSync(true);

      const filesNeedUpload =
        !!excalidrawAPI &&
        LocalData.fileStorage.shouldPreventUnload(
          excalidrawAPI.getSceneElements(),
        );

      if (filesNeedUpload || hadPendingCloudSync) {
        if (import.meta.env.VITE_APP_DISABLE_PREVENT_UNLOAD !== "true") {
          preventUnload(event);
        } else {
          console.warn(
            "preventing unload disabled (VITE_APP_DISABLE_PREVENT_UNLOAD)",
          );
        }
      }
    };
    window.addEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    return () => {
      window.removeEventListener(EVENT.BEFORE_UNLOAD, unloadHandler);
    };
  }, [excalidrawAPI]);

  const onChange = (
    elements: readonly OrderedExcalidrawElement[],
    appState: AppState,
    files: BinaryFiles,
  ) => {
    if (collabAPI?.isCollaborating()) {
      collabAPI.syncElements(elements);
    }

    // this check is redundant, but since this is a hot path, it's best
    // not to evaludate the nested expression every time
    if (!LocalData.isSavePaused()) {
      LocalData.save(elements, appState, files, () => {
        if (excalidrawAPI) {
          let didChange = false;

          const elements = excalidrawAPI
            .getSceneElementsIncludingDeleted()
            .map((element) => {
              if (
                LocalData.fileStorage.shouldUpdateImageElementStatus(element)
              ) {
                const newElement = newElementWith(element, { status: "saved" });
                if (newElement !== element) {
                  didChange = true;
                }
                return newElement;
              }
              return element;
            });

          if (didChange) {
            excalidrawAPI.updateScene({
              elements,
              captureUpdate: CaptureUpdateAction.NEVER,
            });
          }
        }
      });
    }

    // Sync to cloud API if user is logged in and has an active drawing
    // Skip sync until initializeScene has resolved to avoid overwriting DB with empty data
    const currentDrawingId = DrawingStore.getActiveDrawingId();
    if (currentDrawingId && checkIsLoggedIn() && cloudSyncEnabledRef.current) {
      syncDrawingToCloud(currentDrawingId, elements, appState, files);
    }

    // Render the debug scene if the debug canvas is available
    if (debugCanvasRef.current && excalidrawAPI) {
      debugRenderer(
        debugCanvasRef.current,
        appState,
        elements,
        window.devicePixelRatio,
      );
    }
  };

  const [latestShareableLink, setLatestShareableLink] = useState<string | null>(
    null,
  );

  const onExportToBackend = async (
    exportedElements: readonly NonDeletedExcalidrawElement[],
    appState: Partial<AppState>,
    files: BinaryFiles,
  ) => {
    if (exportedElements.length === 0) {
      throw new Error(t("alerts.cannotExportEmptyCanvas"));
    }
    try {
      const { url, errorMessage } = await exportToBackend(
        exportedElements,
        {
          ...appState,
          viewBackgroundColor: appState.exportBackground
            ? appState.viewBackgroundColor
            : getDefaultAppState().viewBackgroundColor,
        },
        files,
      );

      if (errorMessage) {
        throw new Error(errorMessage);
      }

      if (url) {
        setLatestShareableLink(url);
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        const { width, height } = appState;
        console.error(error, {
          width,
          height,
          devicePixelRatio: window.devicePixelRatio,
        });
        throw new Error(error.message);
      }
    }
  };

  const renderCustomStats = (
    elements: readonly NonDeletedExcalidrawElement[],
    appState: UIAppState,
  ) => {
    return (
      <CustomStats
        setToast={(message) => excalidrawAPI!.setToast({ message })}
        appState={appState}
        elements={elements}
      />
    );
  };

  const isOffline = useAtomValue(isOfflineAtom);

  const localStorageQuotaExceeded = useAtomValue(localStorageQuotaExceededAtom);

  const onCollabDialogOpen = useCallback(
    () => setShareDialogState({ isOpen: true, type: "collaborationOnly" }),
    [setShareDialogState],
  );

  // browsers generally prevent infinite self-embedding, there are
  // cases where it still happens, and while we disallow self-embedding
  // by not whitelisting our own origin, this serves as an additional guard
  if (isSelfEmbedding) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          height: "100%",
        }}
      >
        <h1>I'm not a pretzel!</h1>
      </div>
    );
  }

  const ExcalidrawPlusCommand = {
    label: "Excalidraw+",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: ["plus", "cloud", "server"],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_LP
        }/plus?utm_source=excalidraw&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };
  const ExcalidrawPlusAppCommand = {
    label: "Sign up",
    category: DEFAULT_CATEGORIES.links,
    predicate: true,
    icon: <div style={{ width: 14 }}>{ExcalLogo}</div>,
    keywords: [
      "excalidraw",
      "plus",
      "cloud",
      "server",
      "signin",
      "login",
      "signup",
    ],
    perform: () => {
      window.open(
        `${
          import.meta.env.VITE_APP_PLUS_APP
        }?utm_source=excalidraw&utm_medium=app&utm_content=command_palette`,
        "_blank",
      );
    },
  };

  return (
    <div
      style={{ height: "100%" }}
      className={clsx("excalidraw-app", {
        "is-collaborating": isCollaborating,
      })}
    >
      <Excalidraw
        excalidrawAPI={excalidrawRefCallback}
        onChange={onChange}
        initialData={initialStatePromiseRef.current.promise}
        isCollaborating={isCollaborating}
        viewModeEnabled={forceViewMode || undefined}
        onPointerUpdate={collabAPI?.onPointerUpdate}
        UIOptions={{
          canvasActions: {
            toggleTheme: true,
            export: {
              onExportToBackend,
              renderCustomUI: excalidrawAPI
                ? (elements, appState, files) => {
                    return (
                      <ExportToExcalidrawPlus
                        elements={elements}
                        appState={appState}
                        files={files}
                        name={excalidrawAPI.getName()}
                        onError={(error) => {
                          excalidrawAPI?.updateScene({
                            appState: {
                              errorMessage: error.message,
                            },
                          });
                        }}
                        onSuccess={() => {
                          excalidrawAPI.updateScene({
                            appState: { openDialog: null },
                          });
                        }}
                      />
                    );
                  }
                : undefined,
            },
          },
        }}
        langCode={langCode}
        renderCustomStats={renderCustomStats}
        detectScroll={false}
        handleKeyboardGlobally={true}
        autoFocus={true}
        theme={editorTheme}
        renderTopRightUI={(isMobile) => {
          if (isMobile || !collabAPI || isCollabDisabled) {
            return null;
          }

          return (
            <div className="excalidraw-ui-top-right">
              {excalidrawAPI?.getEditorInterface().formFactor === "desktop" && (
                <ExcalidrawPlusPromoBanner
                  isSignedIn={isExcalidrawPlusSignedUser}
                />
              )}

              {collabError.message && <CollabError collabError={collabError} />}
              <LiveCollaborationTrigger
                isCollaborating={isCollaborating}
                onSelect={() =>
                  setShareDialogState({ isOpen: true, type: "share" })
                }
                editorInterface={editorInterface}
              />
            </div>
          );
        }}
        onLinkOpen={(element, event) => {
          if (element.link && isElementLink(element.link)) {
            event.preventDefault();
            excalidrawAPI?.scrollToContent(element.link, { animate: true });
          }
        }}
      >
        <AppMainMenu
          onCollabDialogOpen={onCollabDialogOpen}
          isCollaborating={isCollaborating}
          isCollabEnabled={!isCollabDisabled}
          theme={appTheme}
          setTheme={(theme) => setAppTheme(theme)}
          refresh={() => forceRefresh((prev) => !prev)}
        />
        <AppWelcomeScreen
          onCollabDialogOpen={onCollabDialogOpen}
          isCollabEnabled={!isCollabDisabled}
        />
        <OverwriteConfirmDialog>
          <OverwriteConfirmDialog.Actions.ExportToImage />
          <OverwriteConfirmDialog.Actions.SaveToDisk />
          {excalidrawAPI && (
            <OverwriteConfirmDialog.Action
              title={t("overwriteConfirm.action.excalidrawPlus.title")}
              actionLabel={t("overwriteConfirm.action.excalidrawPlus.button")}
              onClick={() => {
                exportToExcalidrawPlus(
                  excalidrawAPI.getSceneElements(),
                  excalidrawAPI.getAppState(),
                  excalidrawAPI.getFiles(),
                  excalidrawAPI.getName(),
                );
              }}
            >
              {t("overwriteConfirm.action.excalidrawPlus.description")}
            </OverwriteConfirmDialog.Action>
          )}
        </OverwriteConfirmDialog>
        <AppFooter onChange={() => excalidrawAPI?.refresh()} />
        {excalidrawAPI && <AIComponents excalidrawAPI={excalidrawAPI} />}

        <TTDDialogTrigger />
        {isCollaborating && isOffline && (
          <div className="alertalert--warning">
            {t("alerts.collabOfflineWarning")}
          </div>
        )}
        {localStorageQuotaExceeded && (
          <div className="alert alert--danger">
            {t("alerts.localStorageQuotaExceeded")}
          </div>
        )}
        {latestShareableLink && (
          <ShareableLinkDialog
            link={latestShareableLink}
            onCloseRequest={() => setLatestShareableLink(null)}
            setErrorMessage={setErrorMessage}
          />
        )}
        {excalidrawAPI && !isCollabDisabled && (
          <Collab excalidrawAPI={excalidrawAPI} />
        )}

        <ShareDialog
          collabAPI={collabAPI}
          activeDrawingId={DrawingStore.getActiveDrawingId()}
          onExportToBackend={async () => {
            if (excalidrawAPI) {
              try {
                await onExportToBackend(
                  excalidrawAPI.getSceneElements(),
                  excalidrawAPI.getAppState(),
                  excalidrawAPI.getFiles(),
                );
              } catch (error: any) {
                setErrorMessage(error.message);
              }
            }
          }}
        />

        <AppSidebar />

        {errorMessage && (
          <ErrorDialog onClose={() => setErrorMessage("")}>
            {errorMessage}
          </ErrorDialog>
        )}

        <CommandPalette
          customCommandPaletteItems={[
            {
              label: t("labels.liveCollaboration"),
              category: DEFAULT_CATEGORIES.app,
              keywords: [
                "team",
                "multiplayer",
                "share",
                "public",
                "session",
                "invite",
              ],
              icon: usersIcon,
              perform: () => {
                setShareDialogState({
                  isOpen: true,
                  type: "collaborationOnly",
                });
              },
            },
            {
              label: t("roomDialog.button_stopSession"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!collabAPI?.isCollaborating(),
              keywords: [
                "stop",
                "session",
                "end",
                "leave",
                "close",
                "exit",
                "collaboration",
              ],
              perform: () => {
                if (collabAPI) {
                  collabAPI.stopCollaboration();
                  if (!collabAPI.isCollaborating()) {
                    setShareDialogState({ isOpen: false });
                  }
                }
              },
            },
            {
              label: t("labels.share"),
              category: DEFAULT_CATEGORIES.app,
              predicate: true,
              icon: share,
              keywords: [
                "link",
                "shareable",
                "readonly",
                "export",
                "publish",
                "snapshot",
                "url",
                "collaborate",
                "invite",
              ],
              perform: async () => {
                setShareDialogState({ isOpen: true, type: "share" });
              },
            },
            {
              label: "GitHub",
              icon: GithubIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: [
                "issues",
                "bugs",
                "requests",
                "report",
                "features",
                "social",
                "community",
              ],
              perform: () => {
                window.open(
                  "https://github.com/excalidraw/excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: t("labels.followUs"),
              icon: XBrandIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: ["twitter", "contact", "social", "community"],
              perform: () => {
                window.open(
                  "https://x.com/excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: t("labels.discordChat"),
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              icon: DiscordIcon,
              keywords: [
                "chat",
                "talk",
                "contact",
                "bugs",
                "requests",
                "report",
                "feedback",
                "suggestions",
                "social",
                "community",
              ],
              perform: () => {
                window.open(
                  "https://discord.gg/UexuTaE",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            {
              label: "YouTube",
              icon: youtubeIcon,
              category: DEFAULT_CATEGORIES.links,
              predicate: true,
              keywords: ["features", "tutorials", "howto", "help", "community"],
              perform: () => {
                window.open(
                  "https://youtube.com/@excalidraw",
                  "_blank",
                  "noopener noreferrer",
                );
              },
            },
            ...(isExcalidrawPlusSignedUser
              ? [
                  {
                    ...ExcalidrawPlusAppCommand,
                    label: "Sign in / Go to Excalidraw+",
                  },
                ]
              : [ExcalidrawPlusCommand, ExcalidrawPlusAppCommand]),

            {
              label: t("overwriteConfirm.action.excalidrawPlus.button"),
              category: DEFAULT_CATEGORIES.export,
              icon: exportToPlus,
              predicate: true,
              keywords: ["plus", "export", "save", "backup"],
              perform: () => {
                if (excalidrawAPI) {
                  exportToExcalidrawPlus(
                    excalidrawAPI.getSceneElements(),
                    excalidrawAPI.getAppState(),
                    excalidrawAPI.getFiles(),
                    excalidrawAPI.getName(),
                  );
                }
              },
            },
            {
              ...CommandPalette.defaultItems.toggleTheme,
              perform: () => {
                setAppTheme(
                  editorTheme === THEME.DARK ? THEME.LIGHT : THEME.DARK,
                );
              },
            },
            {
              label: t("labels.installPWA"),
              category: DEFAULT_CATEGORIES.app,
              predicate: () => !!pwaEvent,
              perform: () => {
                if (pwaEvent) {
                  pwaEvent.prompt();
                  pwaEvent.userChoice.then(() => {
                    // event cannot be reused, but we'll hopefully
                    // grab new one as the event should be fired again
                    pwaEvent = null;
                  });
                }
              },
            },
          ]}
        />
        {isVisualDebuggerEnabled() && excalidrawAPI && (
          <DebugCanvas
            appState={excalidrawAPI.getAppState()}
            scale={window.devicePixelRatio}
            ref={debugCanvasRef}
          />
        )}
      </Excalidraw>
      {excalidrawAPI && checkIsLoggedIn() && DrawingStore.getActiveDrawingId() && (
        <ElementComments excalidrawAPI={excalidrawAPI} />
      )}
      {checkIsLoggedIn() && (
        <CollabRequestPopup
          collabAPI={collabAPI}
          drawingId={DrawingStore.getActiveDrawingId()}
        />
      )}
    </div>
  );
};

// Cloud sync: debounced save to API when drawing changes
const CLOUD_SYNC_TIMEOUT = 200;
// Backoff schedule for retrying failed saves before giving up to the IDB fallback.
const CLOUD_SYNC_RETRY_DELAYS_MS = [2000, 5000, 10000];

let cloudSyncTimer: ReturnType<typeof setTimeout> | null = null;
let pendingCloudSync: {
  drawingId: string;
  elements: readonly any[];
  appState: Partial<AppState>;
  files: BinaryFiles;
} | null = null;
// Fingerprint of last *successfully* synced content — used to skip saves when nothing changed
let lastSyncedFingerprint: string | null = null;
// Guards re-entrancy when an in-flight save is still awaiting.
let cloudSyncInFlight = false;
// Index into CLOUD_SYNC_RETRY_DELAYS_MS — reset on success.
let cloudSyncRetryAttempt = 0;

type SyncStatus = "idle" | "saving" | "saved" | "error";
let onSyncStatusChange: ((status: SyncStatus) => void) | null = null;

// Computes a lightweight fingerprint of what matters for saving.
// Uses element versions (increment on edits) + file count.
// Ignores appState fields like scroll/zoom/selection that change constantly.
const computeFingerprint = (
  elements: readonly any[],
  files: BinaryFiles,
): string => {
  let versionSum = 0;
  for (const el of elements) {
    versionSum += el.version || 0;
  }
  return `${elements.length}:${versionSum}:${Object.keys(files).length}`;
};

// Persist current pending state into IndexedDB so it survives tab close / process kill.
const persistPendingToIDB = async (data: {
  drawingId: string;
  elements: readonly any[];
  appState: Partial<AppState>;
  files: BinaryFiles;
}) => {
  try {
    await PendingSyncStore.save({
      drawingId: data.drawingId,
      elements: data.elements as any[],
      appState: data.appState as Record<string, any>,
      files: data.files as Record<string, any>,
      savedAt: Date.now(),
      versionSum: computeVersionSum(data.elements),
    });
  } catch {
    // best-effort
  }
};

const doCloudSync = async () => {
  if (cloudSyncInFlight) {
    return;
  }
  const data = pendingCloudSync;
  if (!data || !checkIsLoggedIn() || !data.drawingId) {
    return;
  }
  // Mirror to IDB BEFORE the network call so the data survives even a crash mid-save.
  await persistPendingToIDB(data);

  cloudSyncInFlight = true;
  onSyncStatusChange?.("saving");
  try {
    await apiUpdateDrawing(data.drawingId, {
      elements: data.elements as any[],
      appState: data.appState as Record<string, any>,
    });
    const fileEntries = Object.entries(data.files);
    if (fileEntries.length > 0) {
      await apiSaveFiles(
        data.drawingId,
        fileEntries.map(([id, f]) => ({
          id,
          mimeType: (f as any).mimeType,
          dataURL: (f as any).dataURL,
        })),
      );
    }
    // Only clear pendingCloudSync if no newer edit replaced it during the await.
    if (pendingCloudSync === data) {
      pendingCloudSync = null;
    }
    // Update fingerprint only after server confirms — this is the key fix for the
    // "optimistic fingerprint" bug that made failed saves look successful.
    lastSyncedFingerprint = computeFingerprint(data.elements, data.files);
    cloudSyncRetryAttempt = 0;
    PendingSyncStore.clear(data.drawingId).catch(() => {});
    onSyncStatusChange?.("saved");
  } catch (err) {
    console.error("Cloud sync failed:", err);
    onSyncStatusChange?.("error");
    // Schedule a retry if we still have attempts left. pendingCloudSync stays
    // populated so the next attempt resends the same (or newer) data.
    if (cloudSyncRetryAttempt < CLOUD_SYNC_RETRY_DELAYS_MS.length) {
      const delay = CLOUD_SYNC_RETRY_DELAYS_MS[cloudSyncRetryAttempt++];
      if (cloudSyncTimer) {
        clearTimeout(cloudSyncTimer);
      }
      cloudSyncTimer = setTimeout(doCloudSync, delay);
    }
    // Retries exhausted? pendingCloudSync stays in memory AND IDB so the next
    // edit (or next drawing open) can pick it up.
  } finally {
    cloudSyncInFlight = false;
  }
};

const syncDrawingToCloud = (
  drawingId: string,
  elements: readonly any[],
  appState: Partial<AppState>,
  files: BinaryFiles,
) => {
  // Skip if nothing meaningful changed (e.g., just selection or zoom)
  const fingerprint = computeFingerprint(elements, files);
  if (fingerprint === lastSyncedFingerprint) {
    return;
  }
  // NOTE: do NOT update lastSyncedFingerprint here — only after a successful save.
  // Setting it optimistically would mask failed saves and cause subsequent edits
  // to leave the failed delta orphaned.

  pendingCloudSync = { drawingId, elements, appState, files };
  if (cloudSyncTimer) {
    clearTimeout(cloudSyncTimer);
  }
  cloudSyncTimer = setTimeout(doCloudSync, CLOUD_SYNC_TIMEOUT);
};

// Reset the fingerprint — call when switching drawings
const resetCloudSyncFingerprint = () => {
  lastSyncedFingerprint = null;
  cloudSyncRetryAttempt = 0;
};

// Flush pending cloud sync immediately
const flushCloudSync = (isUnload = false) => {
  if (cloudSyncTimer) {
    clearTimeout(cloudSyncTimer);
    cloudSyncTimer = null;
  }
  if (!pendingCloudSync) {
    return;
  }
  if (isUnload) {
    const data = pendingCloudSync;
    // Persist to IDB synchronously-ish (fire-and-forget). Modern browsers allow
    // IDB writes to complete after unload. This is the safety net when the
    // keepalive request below gets dropped for being >60KB.
    persistPendingToIDB(data);
    if (data.drawingId && checkIsLoggedIn()) {
      apiUpdateDrawingKeepAlive(data.drawingId, {
        elements: data.elements as any[],
        appState: data.appState as Record<string, any>,
      });
    }
    // Don't null out pendingCloudSync — if keepalive failed silently, the next
    // open will recover from IDB.
  } else {
    doCloudSync();
  }
};

/**
 * On drawing open, compare server elements against any pending sync in IDB.
 * If IDB is newer (higher versionSum), the server lost the last batch — return
 * the IDB copy so the caller can use it, and queue a cloud sync to push it back.
 * If server is at or ahead, the pending entry is stale and gets cleared.
 */
const recoverPendingSyncIfNewer = async (
  drawingId: string,
  serverElements: readonly any[],
  serverAppState: Record<string, any>,
): Promise<{
  elements: readonly any[];
  appState: Record<string, any>;
  recovered: boolean;
}> => {
  let pending;
  try {
    pending = await PendingSyncStore.load(drawingId);
  } catch {
    return { elements: serverElements, appState: serverAppState, recovered: false };
  }
  if (!pending) {
    return { elements: serverElements, appState: serverAppState, recovered: false };
  }
  const serverVersionSum = computeVersionSum(serverElements);
  if (pending.versionSum > serverVersionSum) {
    console.warn(
      `[recover] Restaurando edições não sincronizadas do drawing ${drawingId} (local versionSum=${pending.versionSum} > server versionSum=${serverVersionSum})`,
    );
    pendingCloudSync = {
      drawingId,
      elements: pending.elements,
      appState: pending.appState,
      files: pending.files as BinaryFiles,
    };
    if (cloudSyncTimer) {
      clearTimeout(cloudSyncTimer);
    }
    cloudSyncTimer = setTimeout(doCloudSync, CLOUD_SYNC_TIMEOUT);
    return {
      elements: pending.elements,
      appState: pending.appState,
      recovered: true,
    };
  }
  PendingSyncStore.clear(drawingId).catch(() => {});
  return { elements: serverElements, appState: serverAppState, recovered: false };
};

type AppView = "auth" | "dashboard" | "editor" | "admin" | "viewer";

// ---------------------------------------------------------------------------
// URL hash helpers — human-readable drawing URLs
// Format: #/DrawingName--<uuid>
// ---------------------------------------------------------------------------
function buildDrawingHash(id: string, drawingName: string): string {
  const slug = encodeURIComponent((drawingName || "Sem-nome").replace(/\s+/g, "-"));
  return `#/${slug}--${id}`;
}

function isCollabHash(hash: string): boolean {
  return /^#room=/.test(hash);
}

function safeSetDrawingHash(id: string, drawingName: string): void {
  // Não sobrescrever hash de sala de colab — derrubaria a sessão ativa.
  if (isCollabHash(window.location.hash)) {
    return;
  }
  window.location.hash = buildDrawingHash(id, drawingName);
}

function parseDrawingFromHash(hash: string): { id: string } | null {
  // New format: #/Name--UUID
  const newFmt = hash.match(/--([a-f0-9-]{36})$/i);
  if (newFmt && hash.startsWith("#/")) {
    return { id: newFmt[1] };
  }
  // Legacy format: #drawing=UUID
  const legacy = hash.match(/^#drawing=([a-f0-9-]+)$/i);
  if (legacy) {
    return { id: legacy[1] };
  }
  return null;
}

function isDrawingHash(hash: string): boolean {
  return parseDrawingFromHash(hash) !== null;
}

/** Save drawing's folder_id so the dashboard opens in the right folder */
function saveDrawingFolder(folderId: string | null) {
  if (folderId) {
    sessionStorage.setItem("excalidraw-last-folder-id", folderId);
  }
}

const ExcalidrawApp = () => {
  const isCloudExportWindow =
    window.location.pathname === "/excalidraw-plus-export";
  if (isCloudExportWindow) {
    return <ExcalidrawPlusIframeExport />;
  }

  const [view, setView] = useState<AppView>(() => {
    // If there is a view-only share link, go to viewer
    if (window.location.hash.match(/^#view=[a-f0-9]+$/i)) {
      return "viewer";
    }
    // If there is a collaboration link or external scene, go straight to editor
    if (
      window.location.hash.includes("#room=") ||
      window.location.hash.includes("#json=") ||
      window.location.hash.includes("#url=")
    ) {
      return "editor";
    }
    // If URL has a drawing hash (new or legacy format), restore that drawing
    const parsedDrawing = parseDrawingFromHash(window.location.hash);
    if (parsedDrawing && checkIsLoggedIn()) {
      DrawingStore.setActiveDrawingId(parsedDrawing.id);
      return "editor";
    }
    // If logged in and has an active drawing, restore the editor
    const storedDrawingId = DrawingStore.getActiveDrawingId();
    if (checkIsLoggedIn() && storedDrawingId) {
      // Hash will be updated after drawing loads (with proper path)
      return "editor";
    }
    // If logged in, show dashboard; otherwise show auth
    return checkIsLoggedIn() ? "dashboard" : "auth";
  });

  const [user, setUser] = useState<ApiUser | null>(null);
  const [activeDrawingId, setActiveDrawingId] = useState<string | null>(
    DrawingStore.getActiveDrawingId(),
  );
  const [activeDrawingName, setActiveDrawingName] = useState<string>("");
  const [isEditingName, setIsEditingName] = useState(false);
  const [editingName, setEditingName] = useState("");
  const [isRestoring, setIsRestoring] = useState(() => {
    const drawingId = DrawingStore.getActiveDrawingId();
    return !!(checkIsLoggedIn() && drawingId && view === "editor");
  });
  const [syncStatus, setSyncStatus] = useState<SyncStatus>("idle");

  // Editor tag state
  const [allTags, setAllTags] = useState<TagInfo[]>([]);
  const [drawingTags, setDrawingTags] = useState<TagInfo[]>([]);
  const [showEditorTagPicker, setShowEditorTagPicker] = useState(false);
  const [newEditorTagName, setNewEditorTagName] = useState("");

  // View-only shared drawing state
  const [sharedDrawing, setSharedDrawing] = useState<SharedDrawing | null>(null);
  const [sharedError, setSharedError] = useState<string | null>(null);

  // Register sync status callback
  useEffect(() => {
    onSyncStatusChange = setSyncStatus;
    return () => { onSyncStatusChange = null; };
  }, []);

  // Auto-clear "saved" status after 3 seconds
  useEffect(() => {
    if (syncStatus === "saved") {
      const t = setTimeout(() => setSyncStatus("idle"), 3000);
      return () => clearTimeout(t);
    }
  }, [syncStatus]);

  // Load user and restore drawing from API on mount
  useEffect(() => {
    if (!checkIsLoggedIn()) {
      setIsRestoring(false);
      return;
    }

    apiGetMe()
      .then(setUser)
      .catch(() => {
        setIsRestoring(false);
        setView("auth");
      });

    const drawingId = DrawingStore.getActiveDrawingId();
    if (drawingId && view === "editor") {
      apiGetDrawing(drawingId)
        .then(async (drawing) => {
          // Se nesse meio tempo o user iniciou uma sessão de colab,
          // não sobrescrever hash nem localStorage — colab tem prioridade.
          if (isCollabHash(window.location.hash)) {
            setIsRestoring(false);
            return;
          }
          setActiveDrawingName(drawing.name);
          setDrawingTags(drawing.tags || []);
          saveDrawingFolder(drawing.folder_id);
          safeSetDrawingHash(drawingId, drawing.name);

          // If a previous session left unsynced edits in IDB that are newer
          // than the server copy, restore them and queue a re-sync.
          const serverAppStateRaw = drawing.appState || drawing.app_state || {};
          const recovery = await recoverPendingSyncIfNewer(
            drawingId,
            drawing.elements || [],
            serverAppStateRaw,
          );

          // Write fresh elements to localStorage (recovered version if any)
          localStorage.setItem(
            STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS,
            JSON.stringify(recovery.elements),
          );
          // Merge appState: preserve browser-only fields, server fields take priority
          const existing = JSON.parse(
            localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_APP_STATE) || "{}",
          );
          localStorage.setItem(
            STORAGE_KEYS.LOCAL_STORAGE_APP_STATE,
            JSON.stringify({ ...existing, ...recovery.appState }),
          );
          // Hidrata IndexedDB com os arquivos da nuvem para que loadImages()
          // os encontre neste dispositivo (caso de F5 ou abrir via URL direta).
          if (drawing.files && drawing.elements) {
            try {
              await LocalData.fileStorage.saveFiles({
                elements: drawing.elements as any,
                files: drawing.files as BinaryFiles,
              });
            } catch (err) {
              console.error("Falha ao hidratar IndexedDB com arquivos do drawing:", err);
            }
          }
          apiListTags().then(setAllTags).catch(() => {});
        })
        .catch(() => {
          // Drawing not found or deleted — go to dashboard
          DrawingStore.setActiveDrawingId(null);
          window.history.replaceState({}, APP_NAME, window.location.pathname);
          setView("dashboard");
        })
        .finally(() => setIsRestoring(false));
    } else {
      setIsRestoring(false);
    }
  }, []);

  // Load shared drawing when in viewer mode
  useEffect(() => {
    if (view !== "viewer") {
      return;
    }
    const match = window.location.hash.match(/^#view=([a-f0-9]+)$/i);
    if (!match) {
      return;
    }
    apiGetSharedDrawing(match[1])
      .then(setSharedDrawing)
      .catch(() => setSharedError("Link de visualizacao invalido ou expirado."));
  }, [view]);

  const handleAuth = (authedUser: ApiUser) => {
    setUser(authedUser);
    setView("dashboard");
  };

  const handleSkipAuth = () => {
    setView("editor");
  };

  const handleOpenDrawing = async (id: string) => {
    resetCloudSyncFingerprint();
    setActiveDrawingId(id);
    DrawingStore.setActiveDrawingId(id);

    // Load drawing from API and set into localStorage so ExcalidrawWrapper picks it up
    try {
      const drawing = await apiGetDrawing(id);
      setActiveDrawingName(drawing.name);
      setDrawingTags(drawing.tags || []);
      saveDrawingFolder(drawing.folder_id);
      safeSetDrawingHash(id, drawing.name);

      // Check IDB for unsynced edits newer than server copy; restore if found.
      const serverAppStateRaw = drawing.appState || drawing.app_state || {};
      const recovery = await recoverPendingSyncIfNewer(
        id,
        drawing.elements || [],
        serverAppStateRaw,
      );

      // Write to localStorage so the existing initializeScene picks it up
      localStorage.setItem(
        STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS,
        JSON.stringify(recovery.elements),
      );
      // Merge appState: preserve browser-only fields, server fields take priority
      const existingAppState = JSON.parse(
        localStorage.getItem(STORAGE_KEYS.LOCAL_STORAGE_APP_STATE) || "{}",
      );
      localStorage.setItem(
        STORAGE_KEYS.LOCAL_STORAGE_APP_STATE,
        JSON.stringify({ ...existingAppState, ...recovery.appState }),
      );
      // Hidrata IndexedDB local com os arquivos vindos da nuvem para que
      // loadImages() (que lê do IDB no fluxo isInitialLoad) consiga renderizar
      // as imagens neste dispositivo.
      if (drawing.files && drawing.elements) {
        try {
          await LocalData.fileStorage.saveFiles({
            elements: drawing.elements as any,
            files: drawing.files as BinaryFiles,
          });
        } catch (err) {
          console.error("Falha ao hidratar IndexedDB com arquivos do drawing:", err);
        }
      }
      // Load all tags for the picker
      apiListTags().then(setAllTags).catch(() => {});
    } catch (err) {
      console.error("Failed to load drawing:", err);
    }

    setView("editor");
  };

  const handleNewLocalDrawing = () => {
    resetCloudSyncFingerprint();
    // Clear localStorage for a fresh canvas
    localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_ELEMENTS);
    localStorage.removeItem(STORAGE_KEYS.LOCAL_STORAGE_APP_STATE);
    setActiveDrawingId(null);
    DrawingStore.setActiveDrawingId(null);
    setActiveDrawingName("");
    window.history.replaceState({}, APP_NAME, window.location.pathname);
    setView("editor");
  };

  const handleSaveDrawingName = async () => {
    const trimmed = editingName.trim();
    if (trimmed && trimmed !== activeDrawingName && activeDrawingId) {
      try {
        await apiUpdateDrawing(activeDrawingId, { name: trimmed });
        setActiveDrawingName(trimmed);
        // Update URL hash with the new name
        safeSetDrawingHash(activeDrawingId, trimmed);
      } catch (err) {
        console.error("Failed to rename drawing:", err);
      }
    }
    setIsEditingName(false);
  };

  const handleEditorToggleTag = async (tag: TagInfo) => {
    if (!activeDrawingId) return;
    const isActive = drawingTags.some((t) => t.id === tag.id);
    const newTags = isActive
      ? drawingTags.filter((t) => t.id !== tag.id)
      : [...drawingTags, tag];
    setDrawingTags(newTags);
    try {
      await apiSetDrawingTags(activeDrawingId, newTags.map((t) => t.id));
    } catch (err) {
      console.error("Failed to update tags:", err);
      setDrawingTags(drawingTags); // revert
    }
  };

  const handleEditorCreateTag = async () => {
    const name = newEditorTagName.trim();
    if (!name || !activeDrawingId) return;
    try {
      const tag = await apiCreateTag({ name });
      setAllTags((prev) => [...prev, tag]);
      const newTags = [...drawingTags, tag];
      setDrawingTags(newTags);
      await apiSetDrawingTags(activeDrawingId, newTags.map((t) => t.id));
      setNewEditorTagName("");
    } catch (err) {
      console.error("Failed to create tag:", err);
    }
  };

  const handleBackToDashboard = async () => {
    // Force flush any pending saves first so the latest elements are persisted
    LocalData.flushSave();
    flushCloudSync();

    // Generate and save a thumbnail snapshot of the current scene
    const api = activeExcalidrawAPI;
    if (activeDrawingId && api && checkIsLoggedIn()) {
      try {
        const thumbnail = await generateThumbnail(
          api.getSceneElements(),
          api.getAppState(),
          api.getFiles(),
        );
        if (thumbnail) {
          await apiUpdateDrawing(activeDrawingId, { thumbnail });
        }
      } catch (err) {
        console.warn("Failed to save drawing thumbnail:", err);
      }
    }

    setActiveDrawingId(null);
    DrawingStore.setActiveDrawingId(null);
    setActiveDrawingName("");
    window.history.replaceState({}, APP_NAME, window.location.pathname);
    setView("dashboard");
  };

  const handleLogout = () => {
    setUser(null);
    setActiveDrawingId(null);
    DrawingStore.setActiveDrawingId(null);
    window.history.replaceState({}, APP_NAME, window.location.pathname);
    setView("auth");
  };

  if (view === "viewer") {
    if (sharedError) {
      return (
        <TopErrorBoundary>
          <div className="viewer-error">
            <h2>Link indisponivel</h2>
            <p>{sharedError}</p>
          </div>
        </TopErrorBoundary>
      );
    }
    if (!sharedDrawing) {
      return (
        <TopErrorBoundary>
          <div className="dashboard-loading">Carregando visualizacao...</div>
        </TopErrorBoundary>
      );
    }
    return (
      <TopErrorBoundary>
        <Provider store={appJotaiStore}>
          <div className="viewer-header">
            <span className="viewer-header__title">{sharedDrawing.name}</span>
            <span className="viewer-header__author">
              por {sharedDrawing.authorName}
            </span>
            <span className="viewer-header__badge">Somente visualizacao</span>
          </div>
          <div style={{ height: "calc(100vh - 48px)", marginTop: 48 }}>
            <Excalidraw
              initialData={{
                elements: restoreElements(sharedDrawing.elements, null, {
                  repairBindings: true,
                  deleteInvisibleElements: true,
                }),
                appState: {
                  ...restoreAppState(sharedDrawing.appState, null),
                  viewModeEnabled: true,
                  collaborators: new Map(),
                },
                files: sharedDrawing.files || undefined,
                scrollToContent: true,
              }}
              viewModeEnabled={true}
              UIOptions={{
                canvasActions: {
                  toggleTheme: true,
                  export: false,
                  saveAsImage: true,
                },
              }}
            />
          </div>
        </Provider>
      </TopErrorBoundary>
    );
  }

  if (view === "auth") {
    return (
      <TopErrorBoundary>
        <AuthDialog onAuth={handleAuth} onSkip={handleSkipAuth} />
      </TopErrorBoundary>
    );
  }

  if (view === "admin") {
    return (
      <TopErrorBoundary>
        <AdminPanel onBack={() => setView("dashboard")} />
      </TopErrorBoundary>
    );
  }

  if (view === "dashboard") {
    return (
      <TopErrorBoundary>
        <DrawingsDashboard
          user={user}
          onOpenDrawing={handleOpenDrawing}
          onNewLocalDrawing={handleNewLocalDrawing}
          onLogout={handleLogout}
          onAdmin={() => setView("admin")}
          onUserUpdated={setUser}
        />
      </TopErrorBoundary>
    );
  }

  return (
    <TopErrorBoundary>
      <Provider store={appJotaiStore}>
        {isRestoring ? (
          <div className="dashboard-loading">Carregando quadro...</div>
        ) : (
          <ExcalidrawWrapper />
        )}
        {checkIsLoggedIn() && (
          <div className="editor-topbar">
            <button
              className="editor-topbar__back"
              onClick={handleBackToDashboard}
              title="Voltar ao dashboard"
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <polyline points="15 18 9 12 15 6" />
              </svg>
            </button>

            <span className="editor-topbar__sep">/</span>

            {activeDrawingName && (
              isEditingName ? (
                <input
                  className="editor-topbar__name-input"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onBlur={handleSaveDrawingName}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") handleSaveDrawingName();
                    if (e.key === "Escape") setIsEditingName(false);
                  }}
                  autoFocus
                />
              ) : (
                <button
                  className="editor-topbar__name"
                  onClick={() => {
                    setEditingName(activeDrawingName);
                    setIsEditingName(true);
                  }}
                  title="Clique para renomear"
                >
                  {activeDrawingName}
                </button>
              )
            )}

            {activeDrawingId && (
              <>
                {drawingTags.map((tag) => (
                  <span
                    key={tag.id}
                    className="editor-topbar__tag"
                    style={{ backgroundColor: tag.color + "18", color: tag.color, borderColor: tag.color + "40" }}
                  >
                    {tag.name}
                    <button
                      className="editor-topbar__tag-remove"
                      onClick={() => handleEditorToggleTag(tag)}
                      title="Remover tag"
                    >
                      &times;
                    </button>
                  </span>
                ))}
                <div className="editor-topbar__tag-picker">
                  <button
                    className="editor-topbar__add-tag"
                    onClick={() => setShowEditorTagPicker(!showEditorTagPicker)}
                  >
                    + Tag
                  </button>
                  {showEditorTagPicker && (
                    <div className="editor-topbar__tag-dropdown" onMouseLeave={() => setShowEditorTagPicker(false)}>
                      {allTags.filter((t) => !drawingTags.some((dt) => dt.id === t.id)).map((tag) => (
                        <button
                          key={tag.id}
                          className="editor-topbar__tag-option"
                          onClick={() => handleEditorToggleTag(tag)}
                        >
                          <span className="editor-topbar__tag-dot" style={{ backgroundColor: tag.color }} />
                          {tag.name}
                        </button>
                      ))}
                      <div className="editor-topbar__tag-create">
                        <input
                          type="text"
                          placeholder="Nova tag..."
                          value={newEditorTagName}
                          onChange={(e) => setNewEditorTagName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") handleEditorCreateTag();
                          }}
                          className="editor-topbar__tag-create-input"
                        />
                        {newEditorTagName.trim() && (
                          <button className="editor-topbar__tag-create-btn" onClick={handleEditorCreateTag}>
                            Criar
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {syncStatus !== "idle" && (
              <span className={`editor-topbar__sync editor-topbar__sync--${syncStatus}`}>
                {syncStatus === "saving" && "Salvando..."}
                {syncStatus === "saved" && "Salvo"}
                {syncStatus === "error" && "Erro ao salvar"}
              </span>
            )}
          </div>
        )}
      </Provider>
    </TopErrorBoundary>
  );
};

export default ExcalidrawApp;
